import React, { useEffect, useMemo, useState } from 'react';
import { Mail, MessageSquareText, RefreshCw, UserRound } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { legalOperationsService } from '../../../services/legalOperationsService';

const formatDate = (value) =>
  value ? new Intl.DateTimeFormat('ar-SA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '-';

const LegalOperationsMessagesPage = () => {
  const { token } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState({ unreadMessages: 0, unreadConversations: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const query = searchParams.get('q') || '';
  const selectedId = searchParams.get('conversation') || '';

  const loadList = async () => {
    try {
      const [listData, summaryData] = await Promise.all([
        legalOperationsService.getMessages(token),
        legalOperationsService.getMessageSummary(token),
      ]);
      setConversations(listData.conversations || []);
      setSummary(summaryData);
      setError('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return undefined;
    loadList();
    const interval = window.setInterval(loadList, 15000);
    return () => window.clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (!token || !selectedId) {
      setSelected(null);
      return;
    }
    legalOperationsService.getMessageConversation(token, selectedId)
      .then((data) => {
        setSelected(data.conversation);
        loadList();
      })
      .catch((requestError) => setError(requestError.message));
  }, [selectedId, token]);

  const filteredConversations = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return conversations;
    return conversations.filter((conversation) => [
      conversation.client_name,
      conversation.client_phone,
      conversation.reference_number,
      conversation.chat_number,
    ].join(' ').toLowerCase().includes(needle));
  }, [conversations, query]);

  const openConversation = (conversationId) => {
    const next = new URLSearchParams(searchParams);
    next.set('conversation', conversationId);
    setSearchParams(next);
  };

  const updateSearch = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next);
  };

  return (
    <LaborOperationsLayout searchValue={query} onSearchChange={updateSearch}>
      <main className="mx-auto max-w-[1500px] px-6 pb-14 pt-10 sm:px-12" dir="rtl">
        <header className="lo-hero flex flex-wrap items-end justify-between gap-5 px-1 pb-9 pt-2">
          <div>
            <p className="lo-kicker font-bold">INCOMING COMMUNICATIONS</p>
            <h1 className="lo-heading legal-title mt-4 font-bold">المحادثات الواردة</h1>
            <p className="lo-lead mt-3">رسائل العملاء الواردة من بوابة القضايا العمالية ومتابعتها الداخلية.</p>
          </div>
          <button type="button" onClick={loadList} className="lo-control inline-flex h-12 items-center gap-2 rounded-xl px-5 text-[14px] font-bold text-[#293a50]">
            <RefreshCw className="h-4 w-4 text-[#948066]" />
            تحديث
          </button>
        </header>

        <section className="mb-8 flex flex-wrap gap-7 border-y border-[#e7e2d9] py-6">
          <div><p className="text-[14px] text-[#697483]">الرسائل غير المقروءة</p><p className="mt-2 text-[34px] font-bold text-[#293a50]">{summary.unreadMessages || 0}</p></div>
          <div className="border-r border-[#e7e2d9] pr-7"><p className="text-[14px] text-[#697483]">محادثات تحتاج مراجعة</p><p className="mt-2 text-[34px] font-bold text-[#948066]">{summary.unreadConversations || 0}</p></div>
        </section>

        {error && <p className="mb-6 rounded-xl bg-[#f8eee7] px-5 py-4 text-sm font-bold text-[#99623b]">{error}</p>}

        <section className="lo-surface grid min-h-[620px] overflow-hidden rounded-[18px] lg:grid-cols-[390px_1fr]">
          <aside className="border-l border-[#e7e2d9]">
            <div className="border-b border-[#e7e2d9] px-6 py-5">
              <h2 className="legal-title text-[18px] font-bold text-[#293a50]">مركز الرسائل</h2>
            </div>
            <div className="max-h-[690px] overflow-y-auto">
              {filteredConversations.map((conversation) => {
                const lastMessage = conversation.messages?.[0]?.message;
                const active = selectedId === conversation.id;
                return (
                  <button key={conversation.id} type="button" onClick={() => openConversation(conversation.id)} className={`block w-full border-b border-[#eee8de] px-6 py-5 text-right transition ${active ? 'bg-[#f2eee7]' : 'hover:bg-[#faf8f3]'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-[15px] font-bold text-[#293a50]">{conversation.client_name || 'عميل بوابة القضايا'}</p>
                      {conversation.unread_count > 0 && <span className="rounded-full bg-[#293a50] px-2.5 py-1 text-[12px] font-bold text-white">{conversation.unread_count}</span>}
                    </div>
                    <p className="mt-2 text-[13px] text-[#806f58]">{conversation.reference_number || conversation.chat_number}</p>
                    <p className="mt-3 truncate text-[14px] text-[#697483]">{lastMessage || 'لا توجد رسالة'}</p>
                    {conversation.unread_count > 0 && <p className="mt-3 text-[13px] font-bold text-[#948066]">رسالة جديدة</p>}
                  </button>
                );
              })}
              {!loading && !filteredConversations.length && <p className="px-6 py-14 text-center text-sm text-[#8b94a0]">لا توجد رسائل واردة حالياً.</p>}
              {loading && <p className="px-6 py-14 text-center text-sm text-[#8b94a0]">جارٍ تحميل الرسائل...</p>}
            </div>
          </aside>

          <div>
            {!selected ? (
              <div className="flex h-full min-h-[620px] items-center justify-center px-8 text-center">
                <div>
                  <MessageSquareText className="mx-auto h-10 w-10 text-[#b9ad98]" />
                  <p className="mt-5 text-[18px] font-bold text-[#293a50]">اختر محادثة لمراجعة الرسالة</p>
                  <p className="mt-2 text-[14px] text-[#697483]">تظهر الرسائل الجديدة هنا فور تسجيلها في النظام.</p>
                </div>
              </div>
            ) : (
              <>
                <header className="border-b border-[#e7e2d9] px-8 py-6">
                  <div className="flex flex-wrap items-start justify-between gap-5">
                    <div>
                      <h2 className="legal-title text-[20px] font-bold text-[#293a50]">{selected.client_name || 'عميل بوابة القضايا'}</h2>
                      <p className="mt-2 text-[14px] text-[#697483]">{selected.reference_number || selected.chat_number}</p>
                    </div>
                    <span className="rounded-full bg-[#eee8dd] px-4 py-2 text-[13px] font-bold text-[#806f58]">تمت المراجعة</span>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-5 text-[14px] text-[#697483]">
                    <span className="inline-flex items-center gap-2"><UserRound className="h-4 w-4 text-[#948066]" />{selected.client_phone || 'لا يوجد جوال'}</span>
                    <span className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-[#948066]" />{selected.client_email || 'لا يوجد بريد'}</span>
                  </div>
                </header>
                <div className="space-y-4 bg-[#fbfaf7] px-8 py-7">
                  {(selected.messages || []).map((message) => (
                    <article key={message.id} className={`max-w-[78%] rounded-2xl px-5 py-4 ${message.direction === 'incoming' ? 'mr-auto bg-white shadow-[0_1px_3px_rgba(35,43,52,0.07)]' : 'ml-auto bg-[#f1eadf]'}`}>
                      <p className="text-[13px] font-bold text-[#806f58]">{message.sender_name}</p>
                      <p className="mt-2 whitespace-pre-wrap text-[15px] leading-8 text-[#293a50]">{message.message}</p>
                      <p className="mt-3 text-[12px] text-[#8b94a0]">{formatDate(message.created_at)}</p>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      </main>
    </LaborOperationsLayout>
  );
};

export default LegalOperationsMessagesPage;
